import { Link, Navigate } from "react-router-dom";
import { LogOut, ArrowLeft, ArrowRight, ShieldCheck, SlidersHorizontal } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export const Account = () => {
  const { user, logout } = useAuth();

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505] text-zinc-400" data-testid="account-loading">
        <span className="font-display text-sm uppercase tracking-[0.3em]">Checking session…</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="relative min-h-screen bg-[#050505] px-5 py-16 text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <div className="relative mx-auto max-w-lg">
        <Link
          to="/"
          data-testid="account-back-to-site-link"
          className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-[#E60000]"
        >
          <ArrowLeft size={15} />
          Back to site
        </Link>

        <div className="mt-8 border border-white/10 bg-white/[0.03] p-8 md:p-10">
          <div className="mb-8 flex justify-center">
            <Logo />
          </div>
          <h1
            className="font-display text-3xl font-bold uppercase tracking-tight"
            data-testid="account-heading"
          >
            {user.name || "Account"}
          </h1>
          <p className="mt-1 text-sm text-zinc-400" data-testid="account-email">
            {user.email}
          </p>

          <div className="mt-8 flex items-center gap-3 border border-white/10 bg-[#0A0A0A] p-4 text-sm text-zinc-300">
            <ShieldCheck size={18} className="shrink-0 text-[#E60000]" />
            Signed in — role: <span className="font-semibold text-white">{user.role}</span>
          </div>

          {user.role === "admin" && (
            <Link
              to="/admin"
              data-testid="account-owner-dashboard-link"
              className="group mt-4 flex items-center justify-between border border-[#E60000]/60 bg-[#E60000]/10 p-4 transition-[background-color,border-color] duration-300 hover:border-[#E60000] hover:bg-[#E60000]/20"
            >
              <span className="flex items-center gap-3">
                <SlidersHorizontal size={18} className="text-[#E60000]" />
                <span>
                  <span className="font-display block text-sm font-semibold uppercase tracking-[0.2em] text-white">Owner dashboard</span>
                  <span className="block text-xs text-zinc-400">Edit prices, add-ons & contact info</span>
                </span>
              </span>
              <ArrowRight size={18} className="text-[#E60000] transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}

          <button
            onClick={logout}
            data-testid="account-logout-button"
            className="mt-8 flex w-full items-center justify-center gap-2 border border-white/20 py-4 font-display text-sm font-semibold uppercase tracking-[0.25em] text-white transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#E60000] hover:text-[#E60000]"
          >
            <LogOut size={16} />
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Account;
